import type { IDataUnit, IDataUpgrade } from "./interfaces"

const unitPriority: { [name: string]: number } = {
    // Protoss
    Probe: 1,
    Zealot: 2,
    Adept: 3,
    Stalker: 4,
    Sentry: 5,
    HighTemplar: 6,
    DarkTemplar: 7,
    Observer: 8,
    Immortal: 10,
    WarpPrism: 9,
    Colossus: 11,
    Disruptor: 12,
    Phoenix: 13,
    Oracle: 14,
    VoidRay: 15,
    Carrier: 16,
    Tempest: 17,
    Mothership: 18,

    // Terran
    SCV: 1,
    Reaper: 2,
    Marine: 3,
    Marauder: 4,
    Ghost: 5,
    Hellion: 6,
    HellionTank: 7,
    WidowMine: 8,
    Cyclone: 9,
    SiegeTank: 10,
    Thor: 11,
    VikingFighter: 12,
    Medivac: 13,
    Liberator: 14,
    Banshee: 15,
    Raven: 16,
    Battlecruiser: 17,

    // Zerg
    Drone: 1,
    Overlord: 2,
    Queen: 3,
    Zergling: 4,
    Baneling: 5,
    Roach: 6,
    Ravager: 7,
    Overseer: 8,
    Hydralisk: 9,
    LurkerMP: 10,
    SwarmHostMP: 11,
    Infestor: 12,
    Ultralisk: 13,
    Mutalisk: 14,
    Corruptor: 15,
    BroodLord: 16,
    Viper: 17,
}

const structurePriority: { [name: string]: number } = {
    // Protoss
    Nexus: 1,
    Pylon: 2,
    Assimilator: 3,
    Gateway: 4,
    CyberneticsCore: 5,
    Forge: 6,
    ShieldBattery: 7,
    TwilightCouncil: 9,
    PhotonCannon: 8,
    TemplarArchive: 10,
    DarkShrine: 11,
    RoboticsFacility: 12,
    Stargate: 14,
    RoboticsBay: 13,
    FleetBeacon: 15,

    // Terran
    CommandCenter: 1,
    OrbitalCommand: 2,
    PlanetaryFortress: 3,
    SupplyDepot: 4,
    Refinery: 5,
    Barracks: 6,
    BarracksTechLab: 7,
    BarracksReactor: 8,
    Factory: 10,
    FactoryTechLab: 11,
    FactoryReactor: 12,
    Starport: 13,
    StarportTechLab: 14,
    StarportReactor: 15,
    EngineeringBay: 20,
    Armory: 22,
    Bunker: 25,
    GhostAcademy: 26,
    FusionCore: 28,
    SensorTower: 21,
    MissileTurret: 20,

    // Zerg
    Hatchery: 1,
    Lair: 2,
    Hive: 3,
    Extractor: 4,
    SpawningPool: 5,
    EvolutionChamber: 6,
    RoachWarren: 8,
    BanelingNest: 7,
    SpineCrawler: 9,
    SporeCrawler: 10,
    HydraliskDen: 11,
    LurkerDenMP: 12,
    NydusNetwork: 13,
    InfestationPit: 14,
    UltraliskCavern: 17,
    Spire: 19,
    GreaterSpire: 20,
}

const upgradePriority: { [name: string]: number } = {
    // Protoss
    ProtossGroundWeaponsLevel1: 1,
    ProtossGroundWeaponsLevel2: 2,
    ProtossGroundWeaponsLevel3: 3,
    ProtossGroundArmorsLevel1: 4,
    ProtossGroundArmorsLevel2: 5,
    ProtossGroundArmorsLevel3: 6,
    ProtossShieldsLevel1: 7,
    ProtossShieldsLevel2: 8,
    ProtossShieldsLevel3: 9,
    ObserverGraviticBooster: 21,
    GraviticDrive: 22,
    ExtendedThermalLance: 23,
    PsiStormTech: 24,
    ProtossAirWeaponsLevel1: 11,
    ProtossAirWeaponsLevel2: 12,
    ProtossAirWeaponsLevel3: 13,
    ProtossAirArmorsLevel1: 14,
    ProtossAirArmorsLevel2: 15,
    ProtossAirArmorsLevel3: 16,
    WarpGateResearch: 10,
    Charge: 18,
    BlinkTech: 19,
    PhoenixRangeUpgrade: 25,
    AdeptPiercingAttack: 20,
    DarkTemplarBlinkUpgrade: 28,
    VoidRaySpeedUpgrade: 26,
    TempestGroundAttackUpgrade: 27,

    // Terran
    HiSecAutoTracking: 60,
    TerranBuildingArmor: 61,
    TerranInfantryWeaponsLevel1: 4,
    TerranInfantryWeaponsLevel2: 5,
    TerranInfantryWeaponsLevel3: 6,
    TerranInfantryArmorsLevel1: 7,
    TerranInfantryArmorsLevel2: 8,
    TerranInfantryArmorsLevel3: 9,
    Stimpack: 20,
    ShieldWall: 21,
    PunisherGrenades: 22,
    HighCapacityBarrels: 30,
    BansheeCloak: 40,
    PersonalCloaking: 35,
    TerranVehicleWeaponsLevel1: 10,
    TerranVehicleWeaponsLevel2: 11,
    TerranVehicleWeaponsLevel3: 12,
    TerranShipWeaponsLevel1: 13,
    TerranShipWeaponsLevel2: 14,
    TerranShipWeaponsLevel3: 15,
    BattlecruiserEnableSpecializations: 50,
    TerranVehicleAndShipArmorsLevel1: 16,
    TerranVehicleAndShipArmorsLevel2: 17,
    TerranVehicleAndShipArmorsLevel3: 18,
    DrillClaws: 32,
    BansheeSpeed: 41,
    MedivacIncreaseSpeedBoost: 54,
    LiberatorAGRangeUpgrade: 52,
    CycloneLockOnDamageUpgrade: 22,
    TransformationServos: 34,
    EnhancedShockwaves: 36,
    HurricaneThrusters: 31,
    InterferenceMatrix: 42,
    MedivacCaduceusReactor: 51,

    // Zerg
    ZergMeleeWeaponsLevel1: 1,
    ZergMeleeWeaponsLevel2: 2,
    ZergMeleeWeaponsLevel3: 3,
    ZergGroundArmorsLevel1: 7,
    ZergGroundArmorsLevel2: 8,
    ZergGroundArmorsLevel3: 9,
    ZergMissileWeaponsLevel1: 4,
    ZergMissileWeaponsLevel2: 5,
    ZergMissileWeaponsLevel3: 6,
    ZergFlyerWeaponsLevel1: 10,
    ZergFlyerWeaponsLevel2: 11,
    ZergFlyerWeaponsLevel3: 12,
    ZergFlyerArmorsLevel1: 13,
    ZergFlyerArmorsLevel2: 14,
    ZergFlyerArmorsLevel3: 15,
    zerglingmovementspeed: 20,
    zerglingattackspeed: 21,
    overlordspeed: 25,
    Burrow: 26,
    CentrificalHooks: 30,
    GlialReconstitution: 31,
    TunnelingClaws: 32,
    InfestorEnergyUpgrade: 40,
    NeuralParasite: 41,
    MicrobialShroud: 42,
    EvolveGroovedSpines: 50,
    EvolveMuscularAugments: 51,
    Frenzy: 52,
    DiggingClaws: 55,
    LurkerRange: 56,
    ChitinousPlating: 60,
    AnabolicSynthesis: 61,
}

const iconSortUnitFunction = (item1: IDataUnit, item2: IDataUnit): number => {
    return (unitPriority[item1.name] ?? 0) - (unitPriority[item2.name] ?? 0)
}

const iconSortStructureFunction = (item1: IDataUnit, item2: IDataUnit): number => {
    return (structurePriority[item1.name] ?? 0) - (structurePriority[item2.name] ?? 0)
}
const iconSortUpgradeFunction = (item1: IDataUpgrade, item2: IDataUpgrade): number => {
    return (upgradePriority[item1.name] ?? 0) - (upgradePriority[item2.name] ?? 0)
}

export { iconSortStructureFunction, iconSortUnitFunction, iconSortUpgradeFunction }
